import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { StorageContainerType } from 'constants/StorageContainer';
import { environment } from 'environments/environment';
import JSZip from 'jszip';
import { FileReferenceModel } from 'models/generated/Entities.GormCrm.FileReference';
import { DownloadFileInfoModel } from 'models/generated/Storage.Models.DownloadFileInfo';
import { Observable, Subject, firstValueFrom, lastValueFrom, tap } from 'rxjs';

export interface Base64File { fileName: string, base64: string }

export interface IFileInfo extends FileReferenceModel {
	uploadUrl?: string;
}

export enum UploadStatus {
	InProgress = 0,
	Done = 1,
	Error = 2
}

class UploadBlock {
	progress = 0;
	constructor(public readonly blob: Blob, public readonly id: string) { }
}


@Injectable()
export class FileStorageService {

	private uploadUrl = `${environment.gatewayUrl}/api/upload`;
	constructor(private readonly http: HttpClient) { }
	

	getFileName(filePath: string): string {
		return filePath && filePath.indexOf('/') >= 0 ? filePath.split('/').pop() as string : filePath;
	}

	getDownloadUrl(filePath: string, containerName?: StorageContainerType): Promise<string> {
		return firstValueFrom(
			this.http.get(`${this.uploadUrl}/download-url`, {
				params: { containerName: containerName ?? 'attachments', fileName: filePath },
				responseType: 'text'
			}));
	}
	getDownloadUrls(downloadFileList: DownloadFileInfoModel[], containerName?: StorageContainerType): Promise<DownloadFileInfoModel[]> {
		return firstValueFrom(
			this.http.post<DownloadFileInfoModel[]>(`${this.uploadUrl}/download-urls`, downloadFileList, {
				params: { containerName: containerName ?? 'attachments' }
			}));
	}

	async downloadFile(filePath: string, containerName?: StorageContainerType) {

		const downloadUrl = await this.getDownloadUrl(filePath, containerName);
		const response = await fetch(downloadUrl);
		const blob = await response.blob();
		this.downloadLocalBlob(blob, this.getFileName(filePath));
	}

	downloadMultipleFiles(downloadFileList: DownloadFileInfoModel[], folderName?: string, containerName?: StorageContainerType) {
		const maxBatchSize = 10;
		const totalNumberOfFiles = downloadFileList.length;
		const subject = new Subject<number>();
		const zip = new JSZip();

		if (downloadFileList.length == 0) {
			subject.complete();
			return subject.asObservable();
		}

		let addedFiles = 0;
		downloadFileList = [...downloadFileList]; // Clone the array to avoid modifying the original

		(async () => {
			while (downloadFileList.length > 0) {
				await new Promise<void>(async (resolve) => {

					let batch = downloadFileList.splice(0, maxBatchSize);
					const batchEnd = addedFiles + batch.length;

					batch = await this.getDownloadUrls(batch, containerName);
					for (const file of batch) {
						fetch(file.downloadUrl).then(async (response) => {
							const blob = await response.blob();
							zip.file(file.fullFilePath, blob);
							addedFiles++;
							subject.next(addedFiles);
							if (addedFiles == batchEnd) resolve();
						});
					}
				});
			}
		})();

		subject.subscribe(async () => {
			if (addedFiles < totalNumberOfFiles) return;

			this.downloadLocalBlob(await zip.generateAsync({ type: "blob" }), folderName != undefined ? `${folderName}.zip` : 'files.zip');
			subject.complete();
		});

		return subject.asObservable();
	}

	private downloadLocalBlob(blob: Blob, fileName: string) {
		const url = window.URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = fileName;
		a.click();
		window.URL.revokeObjectURL(url);
	}

	getFileReferencesForUpload(filePaths: string[], container?: string) {
		return firstValueFrom(this.http.post<IFileInfo[]>(`${this.uploadUrl}${(container ? '?container=' + container : '')}`, filePaths));
	}

	finalizeUploads(fileReferenceIds: string[]) {
		return firstValueFrom(this.http.post(`${this.uploadUrl}/finalize-uploads`, fileReferenceIds));
	}

	uploadFile(fileUrl: string, file: File): Observable<{ uploaded: number, total: number; }> {

		const uploadUrl = `${environment.fileBlobStorage}${fileUrl.replace(/^\//, '')}`;
		const blockSize = 10 * 1024 * 1024; // Upload chunks of 10 MegaBytes eachs. Adjust up or down depending on performance
		const uploadProgress = new Subject<{ uploaded: number, total: number; }>(); // Progress tracks the percentage of all blocks summed, so the number is arbitrary depending on block size

		let counter = 0;
		let blockList = '';
		const uploadBlocks: UploadBlock[] = [];

		for (let offset = 0; offset < file.size; offset += blockSize) {
			const blockData = file.slice(offset, Math.min(file.size, offset + blockSize)); // BlockBlob blocks must have a maximum size of 4GB, but usually PUT requests are limited to 128MB
			const blockId = btoa((counter++).toString().padStart(8, '0')); // Block IDs must all have the same string length, formatted as Base64
			blockList += `<Latest>${blockId}</Latest>`;
			uploadBlocks.push(new UploadBlock(blockData, blockId));
		}

		const queuedBlocks = [...uploadBlocks];
		const parallelUpload = async () => {
			while (true) {
				const block = queuedBlocks.shift();
				if (!block) break;

				await lastValueFrom(this.http
					.put(`${uploadUrl}&comp=block&blockid=${block.id}`, block.blob, { reportProgress: true, observe: 'events' })
					.pipe(tap(event => {
						if (event.type == HttpEventType.UploadProgress) block.progress = event.total ? Math.round(100 * event.loaded / event.total) : 0;
						else if (event.type == HttpEventType.Response) block.progress = 100;
						else return;

						uploadProgress.next({
							uploaded: uploadBlocks.reduce((sum, block) => sum + block.progress, 0) ?? 0,
							total: uploadBlocks.length * 100
						});
					}))
				);
			}
		};
		const uploadJobs: Promise<void>[] = [];
		for (let i = 0; i < 3; i++) uploadJobs.push(parallelUpload()); // Run three parallel upload jobs, allowing 3 block uploads to be running concurrently
		Promise.all(uploadJobs).then(async _ => {
			// Once all blocks are uploaded, commit the block list to finalize the blob
			await firstValueFrom(this.http.put(`${uploadUrl}&comp=blocklist`, `<?xml version="1.0" encoding="utf-8"?><BlockList>${blockList}</BlockList>`));
			uploadProgress.complete();
		});

		return uploadProgress.asObservable();
	}

	updateFile(fileReference: FileReferenceModel): Promise<FileReferenceModel> {
		return firstValueFrom(this.http.patch<FileReferenceModel>(`${this.uploadUrl}`, fileReference));
	}
	updateFiles(fileReferences: FileReferenceModel[]): Promise<void> {
		return firstValueFrom(this.http.patch<void>(`${this.uploadUrl}/batch`, fileReferences));
	}

	downloadFileByBase64(response: Base64File, filename: string) {
		const byteCharacters = atob(response.base64);
		const byteNumbers = Array.from(byteCharacters).map(char => char.charCodeAt(0));
		const byteArray = new Uint8Array(byteNumbers);
		const blob = new Blob([byteArray], { type: 'application/octet-stream' });

		this.downloadLocalBlob(blob, filename || response.fileName || 'download.pdf'); // fallback name
	}

	getCsv(headers: string[], data: string[][]) {
		const escape = (v: string) => {
			const s = v == null ? '' : String(v);

			return s.includes(';') || s.includes('"') || s.includes('\n') || s.includes('\r')
				? `"${s.replace(/"/g, '""')}"`
				: s;
		};

		const rows = [
			headers.map(escape),
			...data.map(row => row.map(escape)),
		];

		const csv = rows
			.map(row => row.join(';'))
			.join('\r\n');
		return new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
	}

	downloadCsv(headers: string[], data: string[][], filename = 'download.csv') {
		this.downloadLocalBlob(this.getCsv(headers, data), filename);
	}
}
